import {
	Avatar,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItemAvatar,
	ListItemButton,
	ListItemText,
} from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { FragranceAmazonOffersModel, FragranceRakutenOffersModel } from '~/model/model.fragrance.offers';
import { UIService } from '~/service/service.ui';
import { FragranceId } from '~/shared/dto/id';
import { useModel, useService } from '~/shared/service/service.base';
import { AsyncView } from '../view.async';
import { Col, Row, Text } from '../view.box';

export const FragranceOffersDialog = observer<{ fragranceId: FragranceId }>(({ fragranceId }) => {
	const [ui] = useService(UIService);
	const model = useModel(FragranceAmazonOffersModel);
	model.use(fragranceId);

	const open = ui.dialog === FragranceOffersDialog;

	useEffect(() => {
		open && model.fetchNextPage();
	}, [model, open]);

	return (
		<Dialog open={open} fullWidth maxWidth="lg">
			<DialogTitle>Offers from affiliate partners</DialogTitle>
			<DialogContent>
				<AsyncView async={model.async}>
					<List sx={{ height: '50vh' }}>
						<Virtuoso
							data={model.data}
							itemContent={(_, item) => (
								<ListItemButton key={item.url} href={item.url} target="_blank">
									<ListItemAvatar>
										<Avatar src={item.image}></Avatar>
									</ListItemAvatar>
									<ListItemText
										primary={item.title}
										secondary={`${item.price} ${item.currency}${
											item.savingsAmount
												? ` (Savings: ${item.savingsAmount} ${item.currency}, ${item.savingsPercent}%)`
												: ''
										} from ${item.merchant}; Valid until: ${dayjs(item.validUntil).format()}`}
									/>
								</ListItemButton>
							)}
						/>
					</List>
				</AsyncView>
			</DialogContent>
			<DialogActions>
				<Button onClick={ui.hideDialog}>Close</Button>
			</DialogActions>
		</Dialog>
	);
});

export const FragranceRakutenOffersDialog = observer<{ fragranceId: FragranceId }>(({ fragranceId }) => {
	const [ui] = useService(UIService);
	const model = useModel(FragranceRakutenOffersModel);
	model.use(fragranceId);

	const open = ui.dialog === FragranceRakutenOffersDialog;

	useEffect(() => {
		open && model.fetchNextPage();
	}, [model, open]);

	return (
		<Dialog open={open} fullWidth maxWidth="lg">
			<DialogTitle>Offers from Rakuten Advertising</DialogTitle>
			<DialogContent sx={{ height: '70vh' }}>
				<AsyncView async={model.async}>
					<List sx={{ height: '100%' }}>
						<Virtuoso
							data={model.data}
							itemContent={(_, item) => (
								<ListItemButton key={item.linkid} href={item.linkurl!} target="_blank">
									<ListItemAvatar sx={{ mr: 2 }}>
										<Avatar sx={{ width: 150, height: 150 }} src={item.imageurl}></Avatar>
									</ListItemAvatar>
									<ListItemText
										primary={
											<Row>
												<Text flex={1}>{item.productname}</Text>
												<Text variant="body2">
													{item.price?.amount} {item.price?.currency}
													<Text color={'warning.main'} component="span">
														{item.saleprice?.amount
															? ` (Sale: ${item.saleprice.amount} ${item.saleprice.currency}, ${(((item.price?.amount - item.saleprice.amount) / item.price?.amount) * 100).toFixed(2)}%)`
															: ''}
													</Text>
												</Text>
											</Row>
										}
										primaryTypographyProps={{ component: 'div' }}
										secondary={
											<Row gap={4} mt={2} alignItems={'stretch'}>
												<Col
													flex={1}
													gap={1}
													alignItems="flex-start"
													justifyContent={'space-between'}
												>
													<Row gap={1}>
														<Text variant="body2" fontWeight="bold">
															Category:
														</Text>
														<Text variant="body2">
															{item.category?.primary}
															{item.category?.secondary
																? ` / ${item.category.secondary}`
																: ''}
														</Text>
													</Row>
													<Row gap={1}>
														<Text variant="body2" fontWeight="bold">
															Merchant:
														</Text>
														<Text variant="body2">{item.merchantname}</Text>
													</Row>

													{item.description?.short && (
														<Text variant="body2">
															<Text
																mr={1}
																variant="body2"
																fontWeight="bold"
																component="span"
															>
																Short description:
															</Text>
															{item.description?.short}
														</Text>
													)}

													{item.description?.long && (
														<Text variant="body2">
															<Text
																mr={1}
																variant="body2"
																fontWeight="bold"
																component="span"
															>
																Long description:
															</Text>
															{item.description?.long}
														</Text>
													)}
												</Col>
											</Row>
										}
										secondaryTypographyProps={{ component: 'div' }}
									/>
								</ListItemButton>
							)}
						/>
					</List>
				</AsyncView>
			</DialogContent>
			<DialogActions>
				<Button onClick={ui.hideDialog}>Close</Button>
			</DialogActions>
		</Dialog>
	);
});
